import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { AxiosResponse } from 'axios';
import { TbChartBar } from 'react-icons/tb';

import { authorizedAxiosInstance } from '@/clients';
import { FinancialValue } from '@/shared/components/financial-values';
import EmptySection from '@/shared/components/layouts/views/EmptySection';

import { getMetricsByKeys } from '../../configuration';
import { FinancialOverviewInstitutionalStats, FinancialOverviewKeyStatisticsProps } from '../../types';
import { FinancialMetricFormulaTooltip } from '../FinancialMetricFormula';
import { resolveFinancialMetricSparkline } from '../FinancialMetricSparkline/series';
import { FinancialOverviewDescriptions } from '../FinancialOverviewDescriptions';
import { FinancialOverviewMetricCardGrid } from '../FinancialOverviewMetricCardGrid';

export const FinancialOverviewKeyStatistics: FC<FinancialOverviewKeyStatisticsProps> = ({
    allData,
    chartData = [],
    chartRange,
    configuration,
    marketPrice,
    priceAction,
    tickerSymbol,
}) => {
    const [institutionalStats, setInstitutionalStats] = useState<FinancialOverviewInstitutionalStats>({});

    const handleGetInstitutionalStats = useCallback(async () => {
        if (tickerSymbol) {
            try {
                await authorizedAxiosInstance
                    .get(`/api/vendors/fmp/institutional/ownership`, {
                        params: { symbol: tickerSymbol, includeCurrentQuarter: false },
                    })
                    .then((res: AxiosResponse<FinancialOverviewInstitutionalStats[]>) => {
                        if (res?.status === 200) {
                            setInstitutionalStats(res?.data[0] ?? {});
                        }
                    });
            } catch (error) {
                captureException(error);
            }
        }
    }, [tickerSymbol]);

    useEffect(() => {
        handleGetInstitutionalStats();
    }, [handleGetInstitutionalStats]);

    const renderFinancialValue = useCallback(
        (value: number | string | null | undefined, options?: { kind?: 'currency' | 'number'; scalable?: boolean }) => (
            <FinancialValue {...options} value={value} />
        ),
        [],
    );
    const metricContext = useMemo(
        () => ({ institutionalStats, marketPrice, renderFinancialValue, tickerSymbol }),
        [institutionalStats, marketPrice, renderFinancialValue, tickerSymbol],
    );
    const buildMetricItem = (metric: ReturnType<typeof getMetricsByKeys>[number]) => ({
        ...metric,
        action: metric.key === 'stockPrice' ? priceAction : undefined,
        tooltip: metric.formula ? (
            <FinancialMetricFormulaTooltip
                description={metric.description}
                formula={metric.formula}
                label={metric.label}
            />
        ) : undefined,
        value: metric.getValue(allData, metricContext),
    });
    const summaryMetrics = getMetricsByKeys(configuration.summaryMetricKeys).map((metric) => ({
        ...buildMetricItem(metric),
        sparkline: resolveFinancialMetricSparkline(metric.key, chartData, allData),
        sparklineLabel: `${metric.label} trend${chartRange ? ` over ${chartRange}` : ''}`,
    }));
    const detailMetrics = getMetricsByKeys(configuration.detailMetricKeys).map(buildMetricItem);
    const hasFinancialData = Object.entries(allData).some(
        ([key, group]) =>
            key !== 'industryKpis' &&
            group &&
            typeof group === 'object' &&
            Object.values(group).some((value) => value !== undefined && value !== null && value !== ''),
    );

    if (!hasFinancialData && !Object.keys(institutionalStats).length) {
        return (
            <EmptySection
                description='Financial and ownership statistics are not currently available for this company.'
                height='16rem'
                icon={<TbChartBar size='1.25rem' />}
                label='Key Statistics Unavailable'
            />
        );
    }

    return (
        <div>
            <FinancialOverviewMetricCardGrid items={summaryMetrics} showDescriptionTooltips />
            <FinancialOverviewDescriptions
                column={{ xs: 2, sm: 3, md: 4, lg: 6, xl: 8, xxl: 9 }}
                items={detailMetrics}
                style={{ marginTop: '1rem' }}
            />
        </div>
    );
};
